/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import axios from 'axios';
// import {
//   cacheAdapterEnhancer,
// } from 'axios-extensions';
import LRUCache from 'lru-cache';
import _forEach from 'lodash/forEach';
// eslint-disable-next-line import/no-cycle
import reportError from './error-handler/reportError';
import AppConst from '../constants/app.constant';
import DefConst from '../constants/def.constant';
import utilityService from './utility.service';
import auditDataService from './auditData.service';
import ConfigDeviceSettings from '../config/device.config';
// Add a response interceptor
import { setTimeForLayer, setLayerToPendingState, setLayerToFailState } from './layerUpdateTime/changeLayerUpdateTime';
import { getServiceHostPreference } from './utils/getServiceHostPreference';

const httpService = () => {
  const contents = {};
  let GribServiceURL = null;
  let weatherApiCache = {};
  const {
    CancelToken,
  } = axios;
  // Cache configuration
  const defaultCache = new LRUCache({
    ttl: AppConst.httpCacheAge,
    ttlAutopurge: true,
  });
  const gribCache = new LRUCache({
    ttl: AppConst.gribCacheAge,
    ttlAutopurge: true,
  });
  const httpClient = axios.create({
    //adapter: cacheAdapterEnhancer(axios.defaults.adapter),
  });
  httpClient.interceptors.request.use((config) => {
    // Do something before request is sent
    config.msBeforeAjaxCall = Date.now();
    // console.log(config.msBeforeAjaxCall);
    setLayerToPendingState(config.url);
    return config;
  }, (error) => Promise.reject(error));
  httpClient.interceptors.response.use(
    (response) => {
      setTimeout(() => {
        setTimeForLayer(response.config.url);
      }, 1000);
      return response;
    },
    (error) => {
      setLayerToFailState(error.config.url);
      if (error.config) {
        error.config.msAfterAjaxCall = Date.now();
        error.config.timeTakenInMs = error.config.msAfterAjaxCall - error.config
          .msBeforeAjaxCall;
      }
      error.message = `${error.message}: ${JSON.stringify(error)}`;
      console.log(error.message);
      // Any kind of http error will be logged to sentry
      reportError(error);
      // Do something with response error
      return Promise.reject(error);
    },
  );

  function getWeatherUrlApiCache() {
    weatherApiCache = utilityService.getWeatherApiCacheLocalStorage() || {};
  }

  function saveWeatherUrlApiCache(key, url) {
    if (key && url) {
      weatherApiCache[key] = url;
      utilityService.setWeatherApiCacheLocalStorage(weatherApiCache);
    }
  }
  contents.getServiceURL = () => getServiceHostPreference() + AppConst.serviceEndPoint;
  contents.getGribServiceURL = () => {
    if (!GribServiceURL) {
      GribServiceURL = getServiceHostPreference() + AppConst.gribEndPoint;
    }
    return GribServiceURL;
  };
  contents.getProjectionV2 = (start, mid1, mid2, end, ruleset, product) => {
    const versionToUse = ConfigDeviceSettings.settings.apiVersionKey;
    const requestQueryParameters = [`version=${versionToUse || 3}`];
    if (start) {
      requestQueryParameters.push(`start=${start.toUpperCase()}`);
    }
    if (end) {
      requestQueryParameters.push(`end=${end.toUpperCase()}`);
    }
    if (product) {
      requestQueryParameters.push(`product=${product.toUpperCase()}`);
    }
    if (ruleset) {
      requestQueryParameters.push(`ruleset=${ruleset}`);
    }
    if (mid1) {
      requestQueryParameters.push(`mid1=${mid1}`);
    }
    if (mid2) {
      requestQueryParameters.push(`mid2=${mid2}`);
    }
    // return "static/data/forecast.json";
    return contents.getServiceURL() + requestQueryParameters.join('&');
  };
  contents.getMapMosProjection = (lat1, lng1, lat2, lng2, num) => {
    const requestQueryParameters = [];
    requestQueryParameters.push(`lat1=${lat1}`);
    requestQueryParameters.push(`lng1=${lng1}`);
    requestQueryParameters.push(`lng2=${lng2}`);
    requestQueryParameters.push(`lat2=${lat2}`);
    requestQueryParameters.push(`num=${num}`);
    return getServiceHostPreference() + AppConst.mapMosService + requestQueryParameters
      .join('&');
  };
  contents.getAloftProjection = (lat, lng, maxAlt) => {
    const requestQueryParameters = [];
    requestQueryParameters.push(`lat=${lat}`);
    requestQueryParameters.push(`long=${lng}`);
    requestQueryParameters.push(
      `alt=${DefConst.altServiceOptions[maxAlt || DefConst.defaultMaxAltAllowed]}`,
    );
    return contents.getGribServiceURL() + requestQueryParameters.join('&');
  };
  contents.getApiWeatherGovProjection = async (lat, lng) => {
    getWeatherUrlApiCache();
    if (weatherApiCache[`${lat},${lng}`]) {
      return weatherApiCache[`${lat},${lng}`];
    }
    const data = await contents.http_get(`${getServiceHostPreference()}${AppConst.weatherApi}points/${lat},${lng}`, false, true);
    if (data && data.properties && data.properties.forecast) {
      console.log(data.properties.forecast);
      const forecastUrl = data.properties.forecast.replace('https://api.weather.gov/', `${getServiceHostPreference()}${AppConst.weatherApi}`);
      console.log(forecastUrl);
      saveWeatherUrlApiCache(`${lat},${lng}`, forecastUrl);
      return forecastUrl;
    }
    return null;
  };
  contents.getForecastWeatherGovProjection = (lat, lng) => `${getServiceHostPreference() + AppConst.forecastWeatherGov + lat},${lng}`;
  contents.showingRetry = false;
  contents.excludedFromRetry = ['/airports.json', '/geoMap.geojson', '/api/a-awg/', 'api/b-awg/',
    'api/c-awg/gis/scripts/GairmetJSON.php', '/api/login', 'grib/get-grib', '/radar',
  ];
  contents.checkIfRetryIsAllowed = (url) => {
    let allowRetry = true;
    _forEach(contents.excludedFromRetry, (item) => {
      if (url.indexOf(item) > -1) {
        allowRetry = false;
      }
    });
    return allowRetry;
  };
  contents.attachDataToAuditService = (data) => {
    const recordsToExclude = ['airports.json', 'category.json', 'geoMap.geojson',
      'mapDarkStyle.json', 'mapLightStyle.json', 'workshop_category.json',
      'workshop_type.json', 'workshop.json', 'imagery.json', 'iap-dev/api/', 'iap/api/',
    ];
    let shouldBeExcluded = false;
    const refObj = {};
    _forEach(recordsToExclude, (item) => {
      if (data.request.responseURL.indexOf(item) > -1) {
        shouldBeExcluded = true;
      }
    });
    if (!shouldBeExcluded) {
      if (data.headers['content-type'] !== 'application/json') {
        refObj.value = data.request.response;
        auditDataService[data.request.responseURL] = refObj;
      } else {
        auditDataService[data.request.responseURL] = data.request.response;
      }
    }
  };
  contents.http_get_for_store = async (url, cancelToken) => {
    if (!cancelToken) {
      const source = CancelToken.source();
      cancelToken = source.token;
    }
    try {
      const response = await httpClient.get(url, {
        timeout: AppConst.httpRequestTimeouts,
        cancelToken,
      });
      contents.attachDataToAuditService(response);
      if (response) {
        let data;
        // axios caching is passing data by reference so we need this
        if (response.headers['content-type'] === 'application/json') {
          data = utilityService.clone(response.data);
        } else {
          data = response.data;
        }
        return data;
      }
      return null;
    } catch (error) {
      console.log(error);
      return Promise.reject(new Error(error));
    }
  };
  contents.http_get = async (url, customCache, isWeatherRequest, cancelToken) => {
    if (!cancelToken) {
      const source = CancelToken.source();
      cancelToken = source.token;
    }
    try {
      const response = await httpClient.get(url, {
        cache: customCache
          ? gribCache : defaultCache,
        timeout: (customCache === AppConst.apiCaches.gribCache
          ? AppConst.httpRequestTimeoutsGrib
          : AppConst.httpRequestTimeouts),
        cancelToken,
      });
      contents.attachDataToAuditService(response);
      if (response) {
        let data;
        // axios caching is passing data by reference so we need this
        if (response.headers['content-type'] && response.headers['content-type'].indexOf('application/json') > -1) {
          data = utilityService.clone(response.data);
        } else {
          data = response.data;
        }
        return data;
      }
      return null;
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        // console.log(error.response.data);
        // console.log(error.response.status);
        // console.log(error.response.headers);
        if (
          isWeatherRequest
          && (error.response
            && (
              error.response.status === 500
              || error.response.status === 503
              || error.response.status === 404
            )
          )
        ) {
          if (!error.data) {
            error.data = {};
          }
          error.data.serviceUnavailable = true;
          return error.data;
        }
        return null;
      } if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
        return null;
      }
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
      return null;
    }
  };
  contents.http_post = async (url, requestdata) => {
    try {
      const response = await httpClient.post(url, requestdata, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: (url === AppConst.jsonBlobAPI
          ? AppConst.httpRequestTimeoutsJSONBLOB
          : AppConst.httpRequestTimeouts),
      });
      return response;
    } catch (ex) {
      return Promise.reject(new Error(null));
    }
  };
  contents.http_get_content = async (url) => {
    try {
      const response = await httpClient.get(url, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: AppConst.httpRequestTimeouts,
      });
      return response;
    } catch (ex) {
      return Promise.reject(new Error('networkIssue'));
    }
  };
  contents.http_post_multipart_formdata = async (url, formdata) => {
    try {
      const response = await httpClient.post(url, formdata, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: AppConst.httpRequestTimeouts,
      });
      return response;
    } catch (ex) {
      return Promise.reject(new Error('networkIssue'));
    }
  };
  return contents;
};
export default httpService();
